import Select from '../Select';
import {
  EnteredInputs,
  ModalInput,
  StandardModalContent,
} from './ModalStackContext';
import styles from './StandardModalBody.module.css';

/**
 * Truncates the given string down to the given number of characters
 * @param text Text to truncate
 * @param maxLength Number of characters to allow
 */
function truncate(text: string, maxLength?: number) {
  return maxLength && maxLength > 0 ? text.substring(0, maxLength) : text;
}

type StandardModalBodyProps = {
  body?: StandardModalContent['body'];
  inputs?: StandardModalContent['inputs'];
  inputValues?: EnteredInputs;
  onChangeInputs?: React.Dispatch<React.SetStateAction<EnteredInputs>>;
};

/** Standard body for a modal with any given inputs */
export default function StandardModalBody(
  props: StandardModalBodyProps,
): React.ReactElement {
  const { body, inputs, inputValues = {}, onChangeInputs } = props;

  function buildInputs(inputs: ModalInput[]): React.ReactNode {
    return (
      <div className={styles.inputs}>
        {inputs?.map((input) => {
          if (inputValues[input.id] === null && input.value !== undefined) {
            inputValues[input.id] = input.value;
          }

          if (input.params && 'options' in input.params) {
            return (
              <label key={input.id}>
                <span>{input.label}</span>
                <Select
                  value={input.params.options.find(
                    (option) => option.value === inputValues[input.id],
                  )}
                  options={input.params.options}
                  onChange={({ value }: { value: string }) =>
                    onChangeInputs?.({ ...inputValues, [input.id]: value })
                  }
                />
              </label>
            );
          }
          if (input.params && 'rows' in input.params) {
            return (
              <label key={input.id}>
                <span>{input.label}</span>
                <textarea
                  rows={input.params.rows || 1}
                  value={inputValues[input.id] || ''}
                  onChange={(e) => {
                    const value =
                      'maxLength' in input.params
                        ? truncate(e.target.value, input.params.maxLength)
                        : e.target.value;
                    onChangeInputs?.({ ...inputValues, [input.id]: value });
                  }}
                />
              </label>
            );
          }
          return (
            <label key={input.id}>
              <span>{input.label}</span>
              <input
                value={inputValues[input.id] || ''}
                onChange={(e) => {
                  const value =
                    'maxLength' in input.params
                      ? truncate(e.target.value, input.params.maxLength)
                      : e.target.value;
                  onChangeInputs?.({ ...inputValues, [input.id]: value });
                }}
              />
            </label>
          );
        })}
      </div>
    );
  }

  return (
    <div className={styles.content}>
      {body}
      {inputs && buildInputs(inputs)}
    </div>
  );
}
